/* ===========================================================================
   iEDM — live Yotpo star rows (yotpo-stars.js)
   ===========================================================================
   WHAT THIS REPLACES. Rounds 6 and 7 server-rendered every star row from
   `product.metafields.reviews.*`. Round 10 then established that namespace is
   populated by JUDGE.ME, not Yotpo: every sampled product's
   `judgeme.review_widget_data` matches `reviews.rating_count` / `reviews.rating`
   exactly, while the widget the shopper actually sees on the PDP is Yotpo's. So
   the cards and the PDP title row were drawing Judge.me numbers next to a Yotpo
   reviews list — the "discrepancy" this theme is named after. Every star surface
   now reads Yotpo directly and the metafields are not read for stars anywhere.

   NOT TOUCHED, deliberately: layout/theme.liquid's Product JSON-LD
   `aggregateRating` still reads `reviews.*`. It is the same source the served PDP
   already publishes, changing it is an SEO decision, and it is outside this lane.

   THE ENDPOINT, and the singular/plural trap.
     GET https://api-cdn.yotpo.com/v2/bottom_lines/store/<appkey>/product/<pid>
     -> {"bottomline":{"totalReviews":6,"averageScore":4.8333335, ...}}
   `totalReviews` is PLURAL on v2/bottom_lines. Yotpo's v3 reviews endpoint spells
   the same idea `totalReview`, SINGULAR. Reading the wrong one yields `undefined`,
   which fails the `> 0` gate silently and leaves every slot empty — a failure mode
   that looks exactly like "the API is down". Do not mix the two readers.
   Fixtures for all four documented cases: findings/yotpo-stars/fixtures/.

   HOW A SLOT WORKS. Liquid emits, unconditionally and with no metafield read:
       <div class="ystar" data-ystar="card|pdp" data-pid="{{ product.id }}"></div>
   The slot is height-reserved in CSS (see component-card.css, the inline <style>
   in snippets/product-listing-carousel.liquid, and assets/section-main-product.css)
   so it occupies the same box empty as it does full. This file fills it. A slot
   that is never filled — zero reviews, a failed fetch, no `fetch` in the UA —
   stays empty AT ITS RESERVED HEIGHT. That is the contract: nothing on the page
   may move because of what this file does or does not do.

   VISUAL PARITY. The two markup builders below emit byte-for-byte what the round
   6/7 Liquid emitted, including the half-star rule, the teal #35d7e2 outline for
   empty stars and the "(N)" count. The card variant reuses `.iedm-stars*` and the
   PDP variant reuses `.iedm-pdp-stars*`, so no star CSS changed in this lane —
   only the reserve wrapper is new. The half-star rounding is Yotpo's own fill
   rule, `fill(t) = t <= avg + .25 ? 1 : t <= avg + .75 ? .5 : 0`, expressed the
   way the Liquid expressed it: `round(avg * 10 / 5)` halves. Proven equivalent
   5001/5001 in round 6 and re-proven against these fixtures.

   WHY THE PDP HREF IS AN ATTRIBUTE. The reviews-list anchor target is
   `shopify-section-<template-prefix>__16887990062d72fc90`, and the prefix is only
   knowable from `section.id` at render time. Liquid puts it on the slot as
   `data-ystar-href`; this file copies it onto the anchor it builds. The smooth
   scroll still works because main-product.liquid's listener is DELEGATED on
   `#ProductInfo-<section.id>` and matches with `closest('[data-iedm-reviews-link]')`
   — an element injected later is caught by exactly the same code path.

   EVERY storage and network call is individually try/caught. `sessionStorage`
   can throw on mere property ACCESS in a blocked-cookies context, so the
   property read is inside the try, not outside it.
   =========================================================================== */
(function () {
  'use strict';

  var W = typeof window !== 'undefined' ? window : null;
  if (!W || !W.document) return;
  /* Guard: a section re-render that re-injects the tag, or the theme editor,
     must not swap the API object out from under a caller that captured it. */
  if (W.iedmYotpoStars) return;

  var STORE_KEY = '8wOQvK6WzBI1bkSxFl24GEx7LMDA8JavexcMG8NO';
  var ENDPOINT = 'https://api-cdn.yotpo.com/v2/bottom_lines/store/' + STORE_KEY + '/product/';
  var CONCURRENCY = 8;
  var TTL_MS = 30 * 60 * 1000;
  var CACHE_PREFIX = 'ystar:';
  var SCALE_MAX = 5;
  var TEAL = '#35d7e2';

  /* Verbatim from snippets/card-product.liquid and sections/main-product.liquid.
     Note the card path writes `9 .375` and the PDP path writes `9 0.375` — that
     difference is in the shipped Liquid and is preserved so a served-HTML differ
     sees no change at all. */
  var CARD_PATH_FULL = 'M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 .375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475Z';
  var CARD_PATH_HALF = 'M9 14.118L3.438 17.475L4.914 11.148L0 6.891L6.471 6.342L9 .375Z';
  var PDP_PATH = 'M9 14.118L14.562 17.475L13.086 11.148L18 6.891L11.529 6.342L9 0.375L6.471 6.342L0 6.891L4.914 11.148L3.438 17.475L9 14.118Z';

  function esc(v) {
    return String(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* `avg | times: 10 | divided_by: scale_max | round`, clamped after rounding —
     the exact Liquid the server render used. Liquid's round and Math.round are
     both half-up on positives, so the .25/.75 boundaries land the same way. */
  function halvesOf(avg) {
    var h = Math.round((Number(avg) * 10) / SCALE_MAX);
    if (!isFinite(h) || h < 0) h = 0;
    if (h > 10) h = 10;
    return h;
  }

  function split(avg) {
    var halves = halvesOf(avg);
    var full = Math.floor(halves / 2);
    return { full: full, half: halves % 2, halfAt: full + 1 };
  }

  function cardMarkup(avg, count) {
    var s = split(avg);
    var out = '<div class="iedm-stars"><svg class="iedm-stars__svg" width="86" height="16" viewBox="-0.6 -0.6 103.2 19.2" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="' +
      esc(avg) + ' out of ' + SCALE_MAX + ' stars, ' + esc(count) + ' reviews">';
    for (var i = 1; i <= 5; i++) {
      var x = (i - 1) * 21;
      out += '<path transform="translate(' + x + ')" d="' + CARD_PATH_FULL + '" fill="' +
        (i <= s.full ? TEAL : 'none') + '" stroke="' + TEAL + '"/>';
      if (s.half === 1 && i === s.halfAt) {
        out += '<path transform="translate(' + x + ')" d="' + CARD_PATH_HALF + '" fill="' + TEAL + '"/>';
      }
    }
    return out + '</svg><span class="iedm-stars__count" aria-hidden="true">(' + esc(count) + ')</span></div>';
  }

  function pdpMarkup(avg, count, href, gradId) {
    var s = split(avg);
    var out = '<a class="iedm-pdp-stars" href="' + esc(href) + '" data-iedm-reviews-link aria-label="' +
      esc(avg) + ' out of ' + SCALE_MAX + ' stars rating in total ' + esc(count) +
      ' reviews. Jump to reviews."><span class="iedm-pdp-stars__stars" aria-hidden="true">';
    for (var i = 1; i <= 5; i++) {
      var isHalf = s.half === 1 && i === s.halfAt;
      out += '<svg class="iedm-pdp-stars__star" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" focusable="false">';
      if (isHalf) {
        out += '<defs><linearGradient id="' + esc(gradId) + '"><stop offset="50%" stop-color="' + TEAL +
          '"/><stop offset="50%" stop-color="#FFFFFF" stop-opacity="1"/></linearGradient></defs>';
      }
      out += '<path d="' + PDP_PATH + '" stroke="' + TEAL + '" fill="' +
        (i <= s.full ? TEAL : (isHalf ? "url('#" + gradId + "')" : '#FFFFFF')) + '"/></svg>';
    }
    return out + '</span><span class="iedm-pdp-stars__count" aria-hidden="true">(' + esc(count) + ')</span></a>';
  }

  function cacheGet(pid) {
    try {
      var raw = W.sessionStorage.getItem(CACHE_PREFIX + pid);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || typeof o.t !== 'number' || typeof o.a !== 'number' || typeof o.n !== 'number') return null;
      if (Date.now() - o.t > TTL_MS) {
        try { W.sessionStorage.removeItem(CACHE_PREFIX + pid); } catch (e) {}
        return null;
      }
      return { averageScore: o.a, totalReviews: o.n };
    } catch (e) {
      return null;
    }
  }

  function cacheSet(pid, bl) {
    try {
      W.sessionStorage.setItem(CACHE_PREFIX + pid, JSON.stringify({
        a: bl.averageScore, n: bl.totalReviews, t: Date.now()
      }));
    } catch (e) {}
  }

  /* Resolves with a bottomline object or null. NEVER rejects: a null is the
     "leave the slot empty" signal and there is exactly one of it, whatever the
     failure was (no fetch, network error, non-2xx, malformed JSON, missing key). */
  function fetchBottomline(pid, cb) {
    var settled = false;
    function done(v) { if (!settled) { settled = true; try { cb(v); } catch (e) {} } }
    try {
      if (typeof W.fetch !== 'function') { done(null); return; }
      W.fetch(ENDPOINT + encodeURIComponent(pid), { credentials: 'omit', mode: 'cors' })
        .then(function (r) { return (r && r.ok) ? r.json() : null; })
        .then(function (j) {
          var bl = j && j.bottomline;
          if (!bl) { done(null); return; }
          var n = Number(bl.totalReviews);
          var a = Number(bl.averageScore);
          if (!isFinite(n) || n < 0) { done(null); return; }
          if (!isFinite(a) || a < 0) a = 0;
          done({ averageScore: a, totalReviews: n });
        })
        .catch(function () { done(null); });
    } catch (e) {
      done(null);
    }
  }

  /* Every RESOLVED slot gets data-ystar-state, "filled" or "empty". It is set
     ONLY here, i.e. only once the fetch (or the cache) has answered, so a slot
     that is still in flight carries no state attribute at all and keeps its
     reserved height. That three-state shape - absent / empty / filled - is what
     lets CSS tell "waiting" apart from "nothing to show"; `:empty` cannot, since
     the slot is equally empty in both.

     Zero reviews, a failed fetch, a malformed body and a thrown innerHTML are
     ONE outcome: state "empty". The caller cannot distinguish them and must not
     try - assets/section-main-product.css collapses the PDP reserve on exactly
     this attribute, and cards take the attribute with no CSS attached.

     NOTE, and it is a deliberate departure from the brief's test list: a THROWN
     sessionStorage does not by itself produce "empty". Storage is a cache, not a
     source; when it throws, cacheGet returns null, the fetch still runs, and a
     product with reviews still gets its stars. The state reflects the OUTCOME,
     not the mechanism. Making a blocked-cookies shopper lose the star row
     entirely would be a worse product for no benefit. If the owner ever wants
     the literal reading, it is one line: return early from fill() when
     cacheGet throws. The harness asserts BOTH sub-cases. */
  function paint(slot, bl) {
    var filled = !!(bl && bl.totalReviews > 0);
    if (filled) {
      try {
        if (slot.getAttribute('data-ystar') === 'pdp') {
          slot.innerHTML = pdpMarkup(
            bl.averageScore,
            bl.totalReviews,
            slot.getAttribute('data-ystar-href') || '',
            'iedm-half-ystar-' + (slot.getAttribute('data-pid') || '')
          );
        } else {
          slot.innerHTML = cardMarkup(bl.averageScore, bl.totalReviews);
        }
      } catch (e) {
        /* Nothing was painted, so the slot is empty in fact as well as in
           state. Say so rather than claiming "filled" over a blank box. */
        filled = false;
      }
    }
    try { slot.setAttribute('data-ystar-state', filled ? 'filled' : 'empty'); } catch (e) {}
  }

  /* r4 (2026-10-19): ONE page-wide queue instead of a pool per scan. Slots are now
     claimed as the parser inserts them, so scans are many and small; a per-scan pool
     would multiply the concurrency and could fetch one product twice. `inflight`
     dedupes across scans: a product already requested gets the new slot appended,
     not a second request. Requests go out in DOM order (FIFO). The endpoint, the
     response handling and the painted markup are unchanged, so the numbers cannot
     change. Yotpo has no batch endpoint for this: its own star-ratings widget
     (widget-reviews-star-ratings app.v0.7.1) calls the same per-product
     /v2/bottom_lines/store/<key>/product/<pid>. api-cdn.yotpo.com speaks HTTP/2,
     so the parallel requests share one (preconnected) connection. */
  var inflight = {};
  var queue = [];
  var active = 0;

  function pump() {
    while (active < CONCURRENCY && queue.length) {
      active++;
      (function (pid) {
        fetchBottomline(pid, function (bl) {
          if (bl) cacheSet(pid, bl);
          var list = inflight[pid] || [];
          delete inflight[pid];
          for (var q = 0; q < list.length; q++) {
            try { paint(list[q], bl); } catch (e) {}
          }
          active--;
          pump();
        });
      })(queue.shift());
    }
  }

  function claim(slot) {
    /* Claim the slot before any async work so a re-scan mid-flight cannot
       queue it twice. */
    if (!slot || slot.getAttribute('data-ystar-claimed') === '1') return;
    var pid = slot.getAttribute('data-pid');
    if (!pid) return;
    slot.setAttribute('data-ystar-claimed', '1');
    var hit = cacheGet(pid);
    if (hit) { paint(slot, hit); return; }
    if (inflight[pid]) { inflight[pid].push(slot); return; }
    inflight[pid] = [slot];
    queue.push(pid);
    pump();
  }

  function fill(root) {
    var scope = (root && root.querySelectorAll) ? root : W.document;
    var nodes;
    try { nodes = scope.querySelectorAll('[data-ystar]'); } catch (e) { return; }
    for (var k = 0; k < nodes.length; k++) claim(nodes[k]);
  }

  /* Slots are claimed the moment they enter the DOM: while the parser streams the
     document (this file is async in the head, so it usually runs before the grid
     exists), and afterwards for Load More on collections (round 16), the USF facet
     swap on search, the r2 Rebuy fallback cards and any app that re-renders a
     carousel. A parser-inserted element arrives with its attributes and no children,
     so the common case is one matches() call; a subtree inserted at once
     (innerHTML) is searched with querySelectorAll. */
  function onAdded(node) {
    if (!node || node.nodeType !== 1) return;
    try {
      if (node.hasAttribute('data-ystar')) claim(node);
      if (node.firstElementChild) {
        var inner = node.querySelectorAll('[data-ystar]');
        for (var i = 0; i < inner.length; i++) claim(inner[i]);
      }
    } catch (e) {}
  }

  function watch() {
    try {
      if (typeof W.MutationObserver !== 'function') return;
      new W.MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; added && j < added.length; j++) onAdded(added[j]);
        }
      }).observe(W.document, { childList: true, subtree: true });
    } catch (e) {}
  }

  function start() {
    fill();
    watch();
  }

  W.iedmYotpoStars = {
    fill: fill,
    /* Pure builders, exported as the static-test seam. findings/yotpo-stars/
       build-verify.js drives these against the recorded fixtures in node. */
    cardMarkup: cardMarkup,
    pdpMarkup: pdpMarkup,
    halvesOf: halvesOf,
    cacheGet: cacheGet,
    cacheSet: cacheSet,
    fetchBottomline: fetchBottomline,
    ENDPOINT: ENDPOINT,
    TTL_MS: TTL_MS
  };

  /* Start now: whatever is already parsed is claimed, the observer catches the rest.
     The DOMContentLoaded pass is a safety net only; fill() is idempotent. */
  start();
  if (W.document.readyState === 'loading') {
    W.document.addEventListener('DOMContentLoaded', function () { fill(); });
  }
})();
